import React, { useState, useEffect } from 'react';

import {
  CardActionArea,
  CardContent,
  Grid,
  Typography,
  Box,
  IconButton,
  Badge,
} from '@mui/material';
import Card from '@mui/material/Card';
import { useNavigate } from 'react-router-dom';
import { AlertsIndicator } from '../../alerts/alerts-indicator';
import { ManagedProperty } from '../../../system/EntityManager';
import { useIsSmallScreen } from '../../../system/SystemMediaQueriesProvider';
import { CalendarMonth, Bolt } from '@mui/icons-material';
import { usePropertySummaryUnitsQuery } from '../../../../types/generated-types';
import type { PropertyPlmSummary } from '../../../../types/generated-types';

export interface PropertyCardProps {
  property: ManagedProperty;
  detailButton?: boolean;
  showUnits?: boolean;
  showPlmIndicators?: boolean;
  plmSummary?: PropertyPlmSummary | null;
}

export function PropertyCard(props: PropertyCardProps) {
  const {
    property,
    showUnits = false,
    showPlmIndicators = false,
    plmSummary,
  } = props;
  const { title, _id, address, imageUrl } = property || {};
  const { line1, city, state, postalCode } = address;
  const cityText = city ? city + ', ' : ' ';
  // const unitLength = units.length;
  const navigate = useNavigate();
  const defaultPropImgUrl = '/defaultPropImage.jpg'; // '../../../assets/property-f.jpeg';
  const [imageError, setImageError] = useState(false);

  // Reset image error state when imageUrl changes
  useEffect(() => {
    setImageError(false);
  }, [imageUrl]);

  const isSmall = useIsSmallScreen();

  return property ? (
    <Card
      sx={{
        height: isSmall ? 'inherit' : '230px',
        boxShadow: 3,
        maxWidth: '450px',
        width: isSmall ? '350px' : '400px',
        // TODO: Calvin (consult with Robert) We need to have a better strategy for showing which properties have alerts.
        // border: majorAlerts ? '1px solid #FF2222' : '',
      }}
    >
      <CardContent
        sx={{
          height: '100%',
          padding: '0',
          '&:last-child': { paddingBottom: 0 },
        }}
      >
        {/* PLM Event Indicators - Positioned above the clickable content */}
        {showPlmIndicators && (
          <PropertyPlmIndicators
            propertyId={property._id}
            plmSummary={plmSummary}
          />
        )}

        <CardActionArea
          onClick={() => {
            navigate(`/properties/${_id}`);
          }}
          aria-label="property-detail"
          sx={{
            height: '100%',
            padding: '10px',
            ':hover': {
              bgcolor: 'lightblue',
            },
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
            }}
          >
            <Grid container>
              <Grid item xs={6}>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: '1 1 65%',
                  }}
                >
                  <Typography
                    variant="h3"
                    sx={{ textOverflow: 'ellipsis' }}
                    noWrap
                  >
                    {title}
                  </Typography>
                  <Typography
                    color={'rgba(0, 0, 0, 0.65)'}
                    sx={{ textOverflow: 'ellipsis', fontSize: 14 }}
                  >
                    {line1}
                  </Typography>
                  <Typography
                    color={'rgba(0, 0, 0, 0.65)'}
                    sx={{ textOverflow: 'ellipsis', fontSize: 14 }}
                  >
                    {cityText + (state || ' ')}
                  </Typography>
                  <Typography
                    color={'rgba(0, 0, 0, 0.65)'}
                    sx={{ textOverflow: 'ellipsis', fontSize: 14 }}
                  >
                    {postalCode || ' '}
                  </Typography>
                </div>
              </Grid>
              <Grid item xs={6}>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: '1 1 35%',
                    marginLeft: '10px',
                  }}
                >
                  <img
                    alt="Property"
                    src={imageError || !imageUrl ? defaultPropImgUrl : imageUrl}
                    width="100%"
                    style={{
                      objectFit: 'cover',
                      objectPosition: '100% 0',
                      maxHeight: '100px',
                    }}
                    //srcSet={`${imageUrl}?w=30&fit=crop&auto=format&dpr=2 2x`}
                    loading="lazy"
                    onError={() => setImageError(true)}
                    onLoad={() => {
                      // Reset error state when a new image successfully loads
                      if (imageError && imageUrl) {
                        setImageError(false);
                      }
                    }}
                  />
                </div>
              </Grid>
            </Grid>
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                flex: '1 1 54%',
              }}
            >
              <Typography
                color={'#636363'}
                sx={{
                  textOverflow: 'ellipsis',
                  fontSize: 14,
                  fontStyle: 'italic',
                }}
              >
                {property.owner?.name}
              </Typography>
              <Typography
                color={'#636363'}
                sx={{
                  textOverflow: 'ellipsis',
                  fontSize: 14,
                  fontStyle: 'italic',
                }}
              >
                {property.propMgr?.name}
              </Typography>
              <Typography
                color={'#636363'}
                sx={{
                  textOverflow: 'ellipsis',
                  fontSize: 14,
                  fontStyle: 'italic',
                }}
              >
                {property.propMgr?.mobile}
              </Typography>
              <Typography
                color={'#636363'}
                sx={{
                  textOverflow: 'ellipsis',
                  fontSize: 14,
                  fontStyle: 'italic',
                }}
              >
                {property.maintMgr?.name}
              </Typography>
              {showUnits && <PropertyUnitCount propertyId={property._id} />}
            </div>
            <div
              style={{
                flex: '1 1 46%',
                marginTop: '6px',
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
                padding: '4px',
                height: 'fit-content',
              }}
            >
              <AlertsIndicator propertyId={property._id} />
            </div>
          </div>
        </CardActionArea>
      </CardContent>
    </Card>
  ) : null;
}

function PropertyPlmIndicators({
  propertyId,
  plmSummary,
}: {
  propertyId: string;
  plmSummary?: PropertyPlmSummary | null;
}) {
  const navigate = useNavigate();
  const activeCount = plmSummary?.activeCount ?? 0;
  const scheduledCount = plmSummary?.scheduledCount ?? 0;

  return activeCount > 0 || scheduledCount > 0 ? (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 0.5,
        mb: 1,
        p: 1,
        position: 'relative',
        zIndex: 1,
      }}
    >
      {activeCount > 0 && (
        <IconButton
          aria-label="Energy event active"
          size="small"
          title="Energy event active"
          sx={{
            padding: '2px',
            color: '#4caf50',
            backgroundColor: 'rgba(76, 175, 80, 0.1)',
            '&:hover': {
              backgroundColor: 'rgba(76, 175, 80, 0.2)',
            },
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (plmSummary?.firstActiveResponseId) {
              navigate(
                `/plm/event-responses/${plmSummary.firstActiveResponseId}/timeline?returnTo=properties`,
              );
            } else {
              navigate(
                `/plm/calendar?propertyId=${propertyId}&returnTo=/properties`,
              );
            }
          }}
        >
          <Badge
            badgeContent={activeCount > 1 ? activeCount : 0}
            color="success"
          >
            <Bolt fontSize="small" />
          </Badge>
        </IconButton>
      )}
      {scheduledCount > 0 && (
        <IconButton
          aria-label="Energy event scheduled"
          size="small"
          title="Energy event scheduled"
          sx={{
            padding: '2px',
            color: '#2196f3',
            backgroundColor: 'rgba(33, 150, 243, 0.1)',
            '&:hover': {
              backgroundColor: 'rgba(33, 150, 243, 0.2)',
            },
          }}
          onClick={(e) => {
            e.stopPropagation();
            navigate(
              `/plm/calendar?propertyId=${propertyId}&returnTo=/properties`,
            );
          }}
        >
          <Badge
            badgeContent={scheduledCount > 1 ? scheduledCount : 0}
            color="primary"
          >
            <CalendarMonth fontSize="small" />
          </Badge>
        </IconButton>
      )}
    </Box>
  ) : null;
}

function PropertyUnitCount({ propertyId }: { propertyId: string }) {
  const { data, loading } = usePropertySummaryUnitsQuery({
    variables: {
      propertyId,
    },
    skip: !propertyId,
    fetchPolicy: 'cache-and-network',
  });

  const unitCount =
    data?.propertySummaryUnits?.units.reduce(
      (count, unitSummary) => count + unitSummary.count,
      0,
    ) ?? 0;

  return loading ? null : (
    <Typography
      color={'#636363'}
      sx={{ textOverflow: 'ellipsis', fontSize: 14 }}
    >
      {'Units: ' + unitCount}
    </Typography>
  );
}
